import type { Page } from '../../types';

const voucherGuidance =
  '<p>Thank you e-vouchers are held in denominations of &pound;5, and can be issued in multiple units from a minimum of &pound;10 up to a maximum of &pound;200 at a time.</p>' +
  '<p>When awarding a thank you e-voucher to a team (which may consist of 2 or more employees), there is a cap of &pound;200 for the team.</p>' +
  "<p><strong>&pound;10 - &pound;50</strong><br />It is likely that the employee or team has met at least one of the criteria for nomination and completed work, and demonstrated the Cabinet Office Values in a way which deserves recognition. It is likely that the employee's contribution has positively impacted their immediate team.</p>" +
  '<p>Examples: organising a team event, supporting new members of the team or stepping in to help support a colleague at short notice to achieve a business objective, demonstrating clear collaboration.</p>' +
  '<p><strong>&pound;60 - &pound;100</strong><br />It is likely that the employee or team has met more than one of the criteria for nomination and completed work, and demonstrated the Cabinet Office Values to a particularly high standard. It is likely that they have impacted upon their wider business area.</p>' +
  '<p>Example: putting forward cost-saving ideas to SMT which result in positive outcomes or proposing refreshed ways of working to promote trust and empowerment of teams, ensuring that team objectives are delivered efficiently.</p>' +
  '<p><strong>&pound;110 - &pound;200</strong><br />It is likely that the employee or team has met multiple criteria for nomination. They will have demonstrated Cabinet Office values and their actions will have had a positive impact on their business area, or the wider Department.</p>' +
  '<p>Example: contributing to a project conducted by an employee network group, helping to make Cabinet Office a more respectful organisation which promotes inclusion.</p>';

const iybGuidance =
  '<p>When awarding an in-year bonus to a team, the same three levels apply to the team.</p>' +
  '<p>As the team has met the associated criteria for the award, as opposed to a single employee, this value will be split equally between the members of the team being nominated.</p>' +
  '<p>For example, if a team of 4 is awarded a &pound;1000 in-year bonus, each member of the team will receive &pound;250 for their contribution.</p>' +
  '<p><strong>&pound;500</strong><br />It is likely that the employee or team has met a number of the criteria for nomination. They will exceed expectations and go beyond their role or team requirements, demonstrating the Cabinet Office Values and having a significant impact on the achievement of team objectives on a long-term basis.</p>' +
  '<p>Examples: delivering a demanding set of tasks to high quality, effective contribution to the delivery of a specific change, initiative or project or proactively setting up a working group to ensure that a wide range of perspectives are taken into account in decision-making.</p>' +
  '<p><strong>&pound;750</strong><br />It is likely that the employee or team has met most of the criteria for nomination and demonstrated the Cabinet Office Values. Their work or actions will have proved invaluable to the organisation or team and will have a long-term impact. They will have completed the work or action to an outstanding standard.</p>' +
  "<p>Examples: dealing with multiple challenging stakeholders simultaneously and over a sustained period of time, building strong relationships and enhancing the team or Cabinet Office's reputation and enabling the business to meet a key priority, or a team working together to achieve a critical task.</p>" +
  "<p><strong>&pound;1,000</strong><br />It is likely that the employee or team meets all of the criteria for nomination, including demonstration of the Cabinet Office Values. They have performed actions which have a significant and long-term impact on the achievement of Cabinet Office's objectives and the enhancement of the Department's reputation.</p>" +
  '<p>Examples: leading a cross-unit/group/departmental/government review e.g. a capability review or restructure, or taking a lead role in setting up and running a new network for Cabinet Office staff.</p>';

const approval: Page[] = [
  {
    pageType: 'question',
    title: 'Approval',
    name: 'approval',
    hidePageTitle: true,
    questions: [
      {
        questionType: 'radio',
        title: 'Do you wish to change this nomination?',
        name: 'approval-change-nomination',
        hint: 'You can change the reward type or amount',
        defaultValue: 'No',
        options: [
          {
            option: 'Yes',
            questions: [
              {
                questionType: 'select',
                title: 'Reward type',
                name: 'approval-reward-type',
                hintOption: 'Select an option',
                options: [
                  {
                    option: 'In Year Bonus',
                    questions: [
                      {
                        questionType: 'select',
                        athenaType: 'decimal(18,2)',
                        title: 'Reward amount',
                        name: 'approval-iyb-amount',
                        hintOption: 'Select an amount',
                        detailsHeading: 'Guidance on choosing a reward amount',
                        detailsContent: iybGuidance,
                        options: [
                          { option: '\u00A3500', value: '500.00' },
                          { option: '\u00A3750', value: '750.00' },
                          { option: '\u00A31000', value: '1000.00' },
                        ],
                        rules: {
                          required: true,
                        },
                      },
                    ],
                  },
                  {
                    option: 'Voucher',
                    questions: [
                      {
                        questionType: 'select',
                        athenaType: 'decimal(18,2)',
                        title: 'Reward amount',
                        name: 'approval-reward-voucher-amount',
                        hintOption: 'Select an amount',
                        detailsHeading: 'Guidance on choosing a reward amount',
                        detailsContent: voucherGuidance,
                        options: [
                          { option: '\u00A310', value: '10.00' },
                          { option: '\u00A325', value: '25.00' },
                          { option: '\u00A350', value: '50.00' },
                          { option: '\u00A375', value: '75.00' },
                          { option: '\u00A3100', value: '100.00' },
                          { option: '\u00A3150', value: '150.00' },
                          { option: '\u00A3200', value: '200.00' },
                          {
                            option: 'Other',
                            questions: [
                              {
                                questionType: 'textInput',
                                athenaType: 'decimal(18,2)',
                                title: 'Enter voucher amount in pounds',
                                name: 'approval-reward-voucher-other-amount',
                                rules: {
                                  required: true,
                                  decimal: 'decimal(2)',
                                  min: 10,
                                  max: 200,
                                },
                                errorMessages: {
                                  min: 'Voucher amount must be \u00A310 or more',
                                  max: 'Voucher amount must be \u00A3200 or less',
                                },
                              },
                            ],
                          },
                        ],
                        rules: {
                          required: true,
                        },
                      },
                    ],
                  },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          'No',
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textarea',
        title: 'Reward Reason',
        name: 'approval-reward-reason',
        hint: 'The text below will be sent in an email to the **nominee** when this nomination is approved',
        textRows: 12,
        wordCount: 500,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'hidden',
        athenaType: 'decimal(18,2)',
        title: 'Paid amount',
        name: 'paid_amount',
      },
    ],
  },
];

export default approval;
